import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const WINDOWS = [[7, "1 week"], [14, "2 weeks"], [30, "30 days"]];

// "Expected — a known event caused this": the true colour stays; the dot goes quiet, the Summary
// leads with the reason, and those days are kept out of the community's 90-day "normal".
export function ExpectedDialog({ open, onOpenChange, tenantId, name, onDone }) {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(14);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!reason.trim()) { toast.error("Say what the known event is — one line"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/community-pulse/expected`, { reason: reason.trim(), days });
      toast.success(`Marked as expected until ${new Date(`${r.data.until_day}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric" })}`);
      onDone?.(r.data); onOpenChange(false); setReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pulse-expected-dialog">
        <DialogHeader><DialogTitle className="font-heading">Expected — a known event caused this</DialogTitle></DialogHeader>
        <p className="text-sm text-[#57534E] -mt-1">
          {name ? <span className="font-semibold text-[#1C1917]">{name}</span> : "This community"} keeps its true colour. The dot goes quiet,
          the Summary opens with your reason, and these days stay out of what counts as “normal” later.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-[#57534E]">What happened</label>
            <input data-testid="pulse-expected-reason" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} maxLength={140}
              placeholder="e.g. Special-assessment letter mailed Sep 3" onKeyDown={(e) => e.key === "Enter" && save()}
              className="mt-1 w-full rounded-xl border border-sand-200 bg-sand-50 px-3.5 py-2.5 text-sm outline-none focus:border-ink" />
          </div>
          <div>
            <label className="text-xs font-bold text-[#57534E]">For how long</label>
            <div className="mt-1 flex gap-1.5">
              {WINDOWS.map(([d, l]) => (
                <button key={d} type="button" data-testid={`pulse-expected-days-${d}`} onClick={() => setDays(d)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors ${days === d ? "bg-ink text-white border-ink" : "border-sand-200 text-[#57534E] hover:border-ink"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" data-testid="pulse-expected-cancel" onClick={() => onOpenChange(false)} className="rounded-full px-4 py-2 text-sm font-semibold text-[#57534E] hover:bg-sand-100">Cancel</button>
          <button type="button" data-testid="pulse-expected-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink text-white px-4 py-2 text-sm font-bold hover:bg-ink-700 disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Mark as expected
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
